import { useState, useEffect } from 'react'
import { Folder, FolderOpen, Edit, Check, X, AlertCircle } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { conversationDirectoryAPI } from '../../services/api'

interface DirectorySelectorProps {
  conversationId: string
  onDirectoryChange?: (newDirectory: string) => void
}

export function DirectorySelector({ conversationId, onDirectoryChange }: DirectorySelectorProps) {
  const [currentDirectory, setCurrentDirectory] = useState<string>('')
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isActive, setIsActive] = useState(false)

  useEffect(() => {
    loadCurrentDirectory()
  }, [conversationId])

  const loadCurrentDirectory = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await conversationDirectoryAPI.get(conversationId)
      setCurrentDirectory(response.workingDirectory)
      setIsActive(response.isActive)
    } catch (err) {
      console.error('Error loading directory:', err)
      // Don't set error for expected failures (conversation not found, etc.)
      // Instead, show a reasonable default
      setCurrentDirectory('api-server')
      setIsActive(false)
      setError(null) // Clear error to show default state
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = () => {
    setEditValue(currentDirectory)
    setIsEditing(true)
    setError(null)
  }

  const handleSave = async () => {
    if (!editValue.trim()) {
      setError('Directory path cannot be empty')
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      const response = await conversationDirectoryAPI.update(conversationId, editValue.trim())
      setCurrentDirectory(response.workingDirectory)
      setIsActive(true)
      setIsEditing(false)
      
      // Notify parent component
      onDirectoryChange?.(response.workingDirectory)
    } catch (err) {
      console.error('Error updating directory:', err)
      setError(err instanceof Error ? err.message : 'Failed to update directory')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setEditValue('')
    setIsEditing(false)
    setError(null)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  const getDisplayDirectory = () => {
    if (!currentDirectory) return 'Loading...'
    
    // Show relative to home directory if possible
    const home = '/Users/' // Could be enhanced to detect actual home
    if (currentDirectory.startsWith(home)) {
      return `~${currentDirectory.slice(home.length - 1)}`
    }
    
    // Show last 2-3 path segments for readability
    const segments = currentDirectory.split('/')
    if (segments.length > 3) {
      return `.../${segments.slice(-2).join('/')}`
    }
    
    return currentDirectory
  }

  if (loading && !currentDirectory) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
        <Folder className="h-4 w-4" />
        <span>Loading directory...</span>
      </div>
    )
  }

  return (
    <div className="px-2 py-1 mt-2">
      <div className="flex items-center gap-2 text-xs">
        <div className="flex items-center gap-1 flex-1 min-w-0">
          {isActive ? (
            <FolderOpen className="h-3 w-3 text-primary flex-shrink-0" />
          ) : (
            <Folder className="h-3 w-3 text-muted-foreground flex-shrink-0" />
          )}
          
          {isEditing ? (
            <div className="flex items-center gap-1 flex-1">
              <Input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyPress}
                className="h-5 text-xs flex-1 px-1 border-0 bg-muted focus:bg-background"
                placeholder="/path/to/directory"
                autoFocus
              />
              <Button
                size="sm"
                variant="ghost"
                className="h-5 w-5 p-0"
                onClick={handleSave}
                disabled={loading}
              >
                <Check className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-5 w-5 p-0"
                onClick={handleCancel}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <span className="truncate font-mono text-xs text-muted-foreground" title={currentDirectory}>
                {getDisplayDirectory()}
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="h-5 w-5 p-0 flex-shrink-0 opacity-60 hover:opacity-100"
                onClick={handleEdit}
                disabled={loading}
              >
                <Edit className="h-2.5 w-2.5" />
              </Button>
            </div>
          )}
        </div>
        
        {isActive && (
          <div 
            className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400"
            title="MCP client active with this directory"
          >
            <div className="h-1.5 w-1.5 bg-green-500 rounded-full"></div>
            <span className="sr-only">Active</span>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-1 mt-1 text-xs text-destructive">
          <AlertCircle className="h-2.5 w-2.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}